import { useEffect, useState } from 'react';

/**
 * Re-renders the calling component once per second while `active` is true.
 * Returns the current epoch ms; pair with a fixed anchor timestamp to display
 * a live count-up (rest timer, set timer, total session time).
 *
 * Stops the interval when `active` flips to false to avoid wakelock drift on
 * inactive screens.
 */
export function useNowSecond(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [active]);
  return now;
}

/**
 * Format milliseconds as "m:ss" for short stretches and "h:mm:ss" once the
 * elapsed duration crosses the hour mark. Negative or non-finite inputs
 * collapse to "0:00" — the caller can safely pass `now - anchor` without
 * clamping itself.
 */
export function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}
